/**
 * Générateur — Effectuer des conversions d'unités sur des grandeurs composées.
 * Attendu ID : 179 | Niveau : 4ème | Domaine : Grandeurs & mesures (O2)
 *
 * Plan de cartes validé en juillet 2026 (historique, doc prof Ex.1-13) : une
 * carte « Convertir des grandeurs composées » à onze formats (durées, vitesses,
 * capacités, débits). Les énergies en kWj (Ex.14-15) avaient été écartées
 * (choix utilisatrice : unité artificielle et coquille du livre).
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 8) : « Il
 * effectue des conversions d'unités sur des grandeurs composées. » Exemple de
 * réussite : « Il sait convertir des m³/s en L/min et inversement (pour des
 * débits) ; il sait convertir des km/h en m/s et inversement (pour des
 * vitesses). » Cours (19Proport2) : « 1 224 km/h = 1 224 000 m / 3 600 s =
 * 340 m/s », « 1 h = 3 600 s », pas d'arrondi. Le neuf : les DEUX unités
 * changent.
 *   - SORTIES : « Durée → h et min », « Secondes → h/min/s », « Durée entre
 *     horaires » (duree_hmin_trous, duree_hms_trous, duree_horaires_trous :
 *     durées de 6e, gens 650 à 652) ; « Capacités ↔ volumes »
 *     (capacite_volume_trous : 5e, gen 949) ; « Débit (volume/temps) »
 *     (debit_calcul_saisie : un calcul de débit n'est pas une conversion).
 *   - km/h → m/s (vitesse_kmh_ms_saisie) : conversions EXACTES seulement (km/h
 *     multiples de 9 : entier ou demi-entier de m/s), plus d'« arrondis au
 *     dixième » ; une plage par mobile (cycliste 18 à 36 km/h, scooter 27 à 45,
 *     voiture et moto 45 à 126, train 90 à 297) : 20,4 % des couples étaient
 *     invraisemblables (cycliste à 130 km/h).
 *   - m/s → km/h (vitesse_ms_kmh_saisie) : une plage par mobile (nageur 1,2 à
 *     2,5 m/s, sprinteur 8 à 10,4 dont 9,6, guépard 25 à 30, faucon 30 à 80,
 *     skieur 15 à 40) : 40,6 % des couples étaient invraisemblables (nageur à
 *     36 m/s) ; doublon retiré ; 34 couples au lieu de 7 valeurs ; le couple
 *     du son (340 m/s = 1 224 km/h, le cours) n'est dans aucune plage :
 *     sacrifié.
 *   - Comparer (vitesse_comparer_qcm) : l'égalité exacte est la clé une fois
 *     sur trois (l'option « Elles sont égales » n'était jamais juste) ; « Laquelle
 *     est la plus grande, ou sont-elles égales ? ».
 *   - Relier (vitesses_plausibles_relier) RECADRÉ en « la même vitesse » : trois
 *     vitesses en km/h, cinq en m/s dont deux leurres nommés, multiplie_par_3_6
 *     (v × 3,6) et divise_par_60 (v ÷ 60), chacun tiré d'une vitesse de la
 *     liste ; « Deux vitesses sont en trop. » ; tiret cadratin et méthode
 *     soufflée retirés. Avant : 4 ou 5 sources, aucun leurre, des vitesses de
 *     physique (son, satellite, Terre, lumière) et des unités hors attendu
 *     (m/min, km/s, mm/s). Libellé « Relier des vitesses égales ».
 *     Deuxième passe (coordinateur) : tirage par motif de rang, « retirer les
 *     deux extrêmes puis relier par rang » de 100 % à 17 % ; mais « garder les
 *     trois cibles rondes » montait à 88,3 % et 9 km/h était une source dans
 *     77 % des tirages. Troisième passe (coordinateur), mode auto, À ARBITRER :
 *     NOUVELLES FAMILLES de leurres, l'erreur d'un zéro dans 3 600 :
 *     zero_oublie (v × 1 000 ÷ 360, dix fois la bonne valeur) et zero_en_trop
 *     (v × 1 000 ÷ 36 000, le dixième), ronds comme les bonnes cibles (le
 *     second calculé seulement sur un multiple de 18 km/h), deux vitesses
 *     différentes, même famille permise ; multiplie_par_3_6 et divise_par_60
 *     ne sont plus tirés (presque jamais ronds). Sources de 9 à 360 km/h (au
 *     lieu de 252 : un leurre ne tombe entre les cibles que si la plus grande
 *     vitesse dépasse dix fois la plus petite ; jusqu'à 252, la plus petite
 *     valait toujours 9 ou 18 km/h et 9 km/h revenait dans 49 à 60 % des
 *     tirages). Le motif de rang est tiré d'abord selon POIDS_MOTIFS_RELIER,
 *     choisis par optimisation mesurée (voir la section du format). Mesures
 *     avant / après dans le rapport de correction.
 *   - QCM (vitesse_intrus_qcm) RECADRÉ en « Quelle vitesse est égale à 54 km/h ? »
 *     (clé 15 m/s) ; distracteurs multiplie_par_3_6 (194,4 m/s), un_seul_facteur
 *     (÷ 1 000 seul, 0,054 m/s), minute_pour_heure (÷ 60, 0,9 m/s) ; toutes les
 *     options en m/s (avant, l'intruse était toujours la seule vitesse en m/s qui
 *     n'était pas un multiple de 5). Libellé « QCM vitesse égale ».
 *   - Débits (debit_conversion_trous) : deux conversions où les DEUX unités
 *     changent, une dans chaque sens (m³/s → L/min, L/min → m³/s) ; avant,
 *     83,6 % des conversions ne changeaient qu'une unité et le sens inverse
 *     n'existait pas ; 2 m³/s = 120 000 L/min exclu (sacrifié).
 *   - Le mot « débit » reste (il est dans l'attendu, pas dans le cours : à
 *     signaler au professeur).
 *   - Rappel brouillon IMPÉRATIF sur les six formats, juste avant la question
 *     (décision : les conversions se font à la main, comme dans le cours).
 *   - Second chemin : chaque conversion est refaite par un autre chemin (km/h
 *     → m/s : m/s × 3,6 redonne les km/h ; m³/s → L/min : par les L/s), throw
 *     sinon. Aucun solveur test-side ne couvre 179 (l'ancien en-tête parlait de
 *     formats « CALCULÉS » ou « déclaratifs » et d'un format debit_unite_qcm
 *     qui n'existait pas).
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs.
 * Six formats (onze avant), dont deux QCM uniques.
 *
 * ── Révision 2026-09-25 (retour prof, épreuve de Musk vue de l'écran) ─────
 * La carte unique « Convertir des grandeurs composées » et ses six onglets aux
 * noms d'exercices (« km/h → m/s », « Comparer des vitesses », « QCM vitesse
 * égale »…) sont remplacés par TROIS CARTES dont les onglets ne disent que le
 * format (règle prof : un onglet = Saisie, QCM, Relier, Saisie trous,
 * Vrai/Faux ; des noms différents = des cartes différentes) :
 *   « Convertir une vitesse » : Saisie (vitesse_saisie, NEUVE : le sens km/h →
 *     m/s ou m/s → km/h est TIRÉ une fois sur deux, l'attendu dit « et
 *     inversement », le prof relance avec ↻ s'il veut un sens ; les deux
 *     anciennes clés vitesse_kmh_ms_saisie et vitesse_ms_kmh_saisie
 *     disparaissent, leurs tirages restent, _meta.params.sens les distingue),
 *     QCM (vitesse_intrus_qcm), Relier (vitesses_plausibles_relier) : les deux
 *     sont dans le sens du cours, km/h → m/s.
 *   « Comparer deux vitesses » : QCM (vitesse_comparer_qcm), une conversion
 *     puis une comparaison, rien au-dessus de l'attendu.
 *   « Convertir des débits » : Saisie trous (debit_conversion_trous), un seul
 *     format, donc pas d'onglet à l'écran.
 * Cinq formats, dont deux QCM uniques ; contrat des distracteurs inchangé.
 *
 * ── Check calculatrice (prof, 2026-09-25 soir : « n'oublie pas élève de 4e ») ─
 * CALCULATRICE (contenu.calculatrice = true) sur les CINQ formats : 243 000 ÷
 * 3 600, 10,4 × 3,6, 57,5 × 3,6 contre 206 ne se font pas à la main en 4e, et
 * ce n'est pas ce que l'attendu évalue (1 km = 1 000 m, 1 h = 3 600 s, le sens
 * du calcul) ; les débits suivent (même attendu, même écran). La décision du
 * 23/09 « à la main comme dans le cours » est remplacée ; le rappel brouillon
 * reste : le calcul s'écrit avant de taper.
 */

import {
    randInt, randomChoice, shuffle,
    makeTexteTrou, makeSaisie, makeQcm, makeRelier,
} from '../../../utils';
import { frFromScaled, kmhVersMsExact, msVersKmhExact, debitM3sLmin } from './grandeurs';

const ATTENDU_ID = 179;
// Trois cartes depuis le 2026-09-25 : le nom dit ce que fait l'élève, l'onglet ne dit que le format.
const L_VITESSE = 'Convertir une vitesse';
const L_COMPARER = 'Comparer deux vitesses';
const L_DEBITS = 'Convertir des débits';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (règle prof 2026-09-23, par attendu) : IMPÉRATIF sur les cinq
// formats, le calcul s'écrit (1 224 000 m / 3 600 s) avant de le taper. Forme
// maison du parc (gen_149, 162).
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';
/** La calculatrice de l'écran sur les cinq formats (prof 2026-09-25 : ÷ 3,6 et × 3,6 sur des décimaux ne se font pas à la main en 4e) ; lue par calculatriceAutorisee. */
const calculatrice = (payload) => ({ ...payload, contenu: { ...payload.contenu, calculatrice: true } });

/** Les multiples de 9 de min à max : en km/h, ils se convertissent exactement en m/s (9 km/h = 2,5 m/s). */
const multiplesDe9 = (min, max) => {
    const out = [];
    for (let v = Math.ceil(min / 9) * 9; v <= max; v += 9) out.push(v);
    return out;
};

/** km/h → m/s, contrôlé par le chemin inverse : m/s × 3,6 redonne les km/h. */
const versMs = (kmh) => {
    const r = kmhVersMsExact(kmh);
    if (r.dixiemes * 36 !== kmh * 100) throw new Error(`gen_179 : ${kmh} km/h ≠ ${r.txt} m/s`);
    return r;
};

// ═════════ Vitesses ═════════

// Une plage vraisemblable par mobile (revue 2026-09-23).
const MOBILES_KMH = [
    { nom: 'Un cycliste', min: 18, max: 36 },
    { nom: 'Un scooter', min: 27, max: 45 },
    { nom: 'Une voiture', min: 45, max: 130 },
    { nom: 'Une moto', min: 45, max: 130 },
    { nom: 'Un train', min: 90, max: 300 },
];
// En dixièmes de m/s.
const MOBILES_MS = [
    { nom: 'Un nageur', dixiemes: [12, 15, 16, 18, 20, 22, 25] },
    { nom: 'Un sprinteur', dixiemes: [80, 85, 88, 90, 95, 96, 100, 104] },
    { nom: 'Un guépard', dixiemes: [250, 260, 270, 280, 300] },
    { nom: 'Un faucon', dixiemes: [300, 350, 400, 450, 500, 600, 700, 800] },
    { nom: 'Un skieur', dixiemes: [150, 200, 250, 300, 350, 400] },
];

// ─── 1. Convertir une vitesse (saisie) : le sens est tiré une fois sur deux ──
const genKmhVersMs = () => {
    const m = randomChoice(MOBILES_KMH);
    const kmh = randomChoice(multiplesDe9(m.min, m.max));
    const { txt } = versMs(kmh);
    const payload = makeSaisie(txt,
        `<p>${m.nom} roule à ${kmh} km/h.</p>${RAPPEL_BROUILLON}<p><b>Convertis cette vitesse en m/s.</b></p>`,
        { ...opts(L_VITESSE), suffixe: 'm/s' });
    payload._meta.params = { sens: 'kmh_ms', mobile: m.nom, kmh, ms: txt };
    return calculatrice(payload);
};

const genMsVersKmh = () => {
    const m = randomChoice(MOBILES_MS);
    const msD = randomChoice(m.dixiemes);
    const { centiemes, txt } = msVersKmhExact(msD);
    // Second chemin : les km/h trouvés, ÷ 3,6, redonnent les m/s.
    if (centiemes !== msD * 36 || (centiemes * 10) % 360 !== 0 || (centiemes * 10) / 360 !== msD) throw new Error(`gen_179 : ${msD} dixièmes de m/s`);
    const msTxt = frFromScaled(msD, 1);
    const payload = makeSaisie(txt,
        `<p>${m.nom} se déplace à ${msTxt} m/s.</p>${RAPPEL_BROUILLON}<p><b>Convertis cette vitesse en km/h.</b></p>`,
        { ...opts(L_VITESSE), suffixe: 'km/h' });
    payload._meta.params = { sens: 'ms_kmh', mobile: m.nom, ms: msTxt, kmh: txt };
    return calculatrice(payload);
};

// Une seule clé pour les deux sens (2026-09-25) : « et inversement » est dans
// l'attendu, le sens n'est pas un choix du prof.
const genVitesseSaisie = () => (randInt(0, 1) === 0 ? genKmhVersMs() : genMsVersKmh());

// ─── 2. Comparer une vitesse en m/s et une en km/h (QCM) ──────
// m/s de 10 à 70, multiples de 2,5 : leur équivalent en km/h est un entier
// (multiple de 9). Une fois sur trois, la vitesse en km/h est cet équivalent
// exact (clé « Elles sont égales ») ; sinon elle s'en écarte de 1 ou 2 km/h.
const MS_COMPARER = Array.from({ length: 25 }, (_, i) => 100 + 25 * i);   // dixièmes : 10 à 70 m/s

const genComparerVitesse = () => {
    const msD = randomChoice(MS_COMPARER);
    const exact = (msD * 36) / 100;                        // km/h, entier
    if (!Number.isInteger(exact)) throw new Error(`gen_179 : comparer ${msD}`);
    const egales = randInt(1, 3) === 1;
    const kmh = egales ? exact : exact + randomChoice([-2, -1, 1, 2]);
    // Second chemin : comparaison en dixièmes de m/s (km/h × 10 / 3,6 = km/h × 25 / 9).
    const kmhEnDixiemesFois9 = kmh * 25;                    // = 9 × (dixièmes de m/s)
    const msFois9 = msD * 9;
    const msTxt = `${frFromScaled(msD, 1)} m/s`;
    const kmhTxt = `${kmh} km/h`;
    const EGALES = 'Elles sont égales';
    let cle;
    if (msFois9 === kmhEnDixiemesFois9) cle = EGALES;
    else cle = msFois9 > kmhEnDixiemesFois9 ? msTxt : kmhTxt;
    if ((cle === EGALES) !== egales) throw new Error(`gen_179 : comparer ${msTxt} ${kmhTxt}`);
    const payload = makeQcm([msTxt, kmhTxt, EGALES], cle,
        `<p>Deux vitesses sont mesurées : ${msTxt} et ${kmhTxt}.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Laquelle est la plus grande, ou sont-elles égales ?</b></p>`,
        opts(L_COMPARER));
    payload._meta.params = {
        ms: msTxt, kmh: kmhTxt, cle,
        distracteurs: [msTxt, kmhTxt, EGALES].filter((c) => c !== cle).map((c) => (c === EGALES ? 'egalite_supposee' : 'mauvaise_conversion')),
    };
    return calculatrice(payload);
};

// ─── 3. Relier des vitesses égales ────────────────────────────
// Trois vitesses en km/h, multiples de 9 de 9 à 360 (conversions exactes :
// 9 km/h = 2,5 m/s) ; leurs égales en m/s ; deux leurres, chacun l'erreur d'un
// zéro dans 3 600 sur l'une des trois vitesses, deux vitesses différentes
// (décision du coordinateur, troisième passe, mode auto, À ARBITRER) :
//   · zero_oublie  : v × 1 000 ÷ 360, dix fois la bonne valeur ;
//   · zero_en_trop : v × 1 000 ÷ 36 000, le dixième de la bonne valeur.
// Deux leurres de la même famille sont permis (sur deux vitesses différentes).
//
// Ce que le tirage garantit, et pourquoi (mesures des tests jetables) :
//   · les deux leurres sont RONDS (entier ou ,5) comme les bonnes cibles : le
//     leurre zero_en_trop n'est calculé que sur un multiple de 18 km/h (sinon
//     il finit en ,25 ou ,75). « Garder les cibles rondes » n'apprend rien.
//   · le motif de rang (place des trois bonnes cibles parmi les cinq triées)
//     est tiré d'abord, selon POIDS_MOTIFS, puis une configuration de ce motif
//     au hasard (tirage par rejet, borné, uniforme dans le motif). Un leurre ne
//     tombe entre deux bonnes cibles que si la plus grande vitesse dépasse dix
//     fois la plus petite (la plus petite vaut alors 9, 18 ou 27 km/h) ; les
//     motifs sans leurre entre les cibles (123 : deux zero_oublie au-dessus ;
//     345 : deux zero_en_trop au-dessous ; 234 : un de chaque côté) prennent
//     n'importe quelles vitesses. Les poids tiennent ensemble : aucun motif
//     au-dessus de 25 %, « retirer les extrêmes » à 5 %, aucune vitesse dans
//     plus de 30 % des tirages, et les stratégies des chiffres (« deux cibles
//     qui ont les mêmes chiffres, l'une est un piège ») autour de 30 % au plus.
//   · les multiplie_par_3_6 et divise_par_60 de la passe précédente ne sont
//     plus tirés : presque jamais ronds, ils faisaient remonter « garder les
//     cibles rondes » (42 % mesuré avec les quatre familles).
const K_MAX_RELIER = 40;                 // 9k km/h, k de 1 à 40 : 9 à 360 km/h
const CONSIGNE_RELIER = `${RAPPEL_BROUILLON}<p><b>Relie chaque vitesse à la vitesse qui lui est égale.</b> Deux vitesses sont en trop.</p>`;

/** Centièmes de m/s pour une vitesse de 9k km/h : bonne valeur 2,5k m/s ; zéro oublié 25k ; zéro en trop 0,25k. */
const CENTIEMES_RELIER = {
    bonne: (k) => 250 * k,
    zero_oublie: (k) => 2500 * k,
    zero_en_trop: (k) => 25 * k,
};

/** Poids des motifs de rang (en %) : 135, 145, 124 ont un leurre entre deux bonnes cibles. */
export const POIDS_MOTIFS_RELIER = { 135: 25, 123: 20, 145: 20, 345: 20, 124: 10, 234: 5 };
const MOTIFS_ENTRE = new Set(['124', '135', '145']);

/** Les douze affectations des deux leurres : familles et rangs des vitesses (deux vitesses différentes). */
const AFFECTATIONS_RELIER = [
    ...[[0, 1], [0, 2], [1, 2]].map((s) => ({ f: ['zero_oublie', 'zero_oublie'], s })),
    ...[[0, 1], [0, 2], [1, 0], [1, 2], [2, 0], [2, 1]].map((s) => ({ f: ['zero_oublie', 'zero_en_trop'], s })),
    ...[[0, 1], [0, 2], [1, 2]].map((s) => ({ f: ['zero_en_trop', 'zero_en_trop'], s })),
];

/** Les triplets (k1 < k2 < k3) où un leurre peut tomber entre deux bonnes cibles : k3 > 10 k1. */
let TRIPLETS_ENTRE = null;
const tripletsEntre = () => {
    if (TRIPLETS_ENTRE) return TRIPLETS_ENTRE;
    TRIPLETS_ENTRE = [];
    for (let a = 1; 10 * a < K_MAX_RELIER; a++) {
        for (let b = a + 1; b < K_MAX_RELIER; b++) {
            for (let c = Math.max(b + 1, 10 * a + 1); c <= K_MAX_RELIER; c++) TRIPLETS_ENTRE.push([a, b, c]);
        }
    }
    return TRIPLETS_ENTRE;
};

/** Le motif de rang des bonnes cibles parmi les cinq triées : « 135 », « 234 »… */
const motifDeRang = (bonnes, leurres) => [...bonnes.map((v) => [v, true]), ...leurres.map((v) => [v, false])]
    .sort((p, q) => p[0] - q[0])
    .map((p, i) => (p[1] ? i + 1 : 0))
    .filter(Boolean)
    .join('');

/** Une configuration (vitesses k, affectation) si elle est admise : cinq valeurs distinctes, leurres ronds et sous 1 000 m/s ; null sinon. */
const evaluerConfigRelier = (ks, aff) => {
    const bonnes = ks.map(CENTIEMES_RELIER.bonne);
    const leurres = aff.f.map((fam, i) => CENTIEMES_RELIER[fam](ks[aff.s[i]]));
    if (new Set([...bonnes, ...leurres]).size !== 5) return null;
    if (leurres.some((v) => v % 50 !== 0 || v >= 100000)) return null;
    return { ks, aff, bonnes, leurres, motif: motifDeRang(bonnes, leurres) };
};

/** Tirage par rejet d'une configuration du motif demandé, uniforme parmi les configurations de ce motif. */
const tirerConfigRelier = (motif) => {
    const entre = MOTIFS_ENTRE.has(motif);
    const kListe = Array.from({ length: K_MAX_RELIER }, (_, i) => i + 1);
    for (let essai = 0; essai < 3000; essai++) {
        const ks = entre ? randomChoice(tripletsEntre()) : shuffle(kListe).slice(0, 3).sort((p, q) => p - q);
        const cfg = evaluerConfigRelier(ks, randomChoice(AFFECTATIONS_RELIER));
        if (cfg && cfg.motif === motif) return cfg;
    }
    return null;
};

// Replis déterministes vérifiés (un par motif), servis si le rejet échoue.
const REPLIS_RELIER = {
    123: { ks: [3, 10, 20], aff: { f: ['zero_oublie', 'zero_oublie'], s: [0, 1] } },     // 27, 90, 180 km/h ; 75 et 250 m/s
    124: { ks: [1, 5, 40], aff: { f: ['zero_oublie', 'zero_oublie'], s: [0, 1] } },      // 9, 45, 360 km/h ; 25 et 125 m/s
    135: { ks: [1, 5, 20], aff: { f: ['zero_oublie', 'zero_en_trop'], s: [0, 2] } },     // 9, 45, 180 km/h ; 25 et 5 m/s
    145: { ks: [1, 20, 30], aff: { f: ['zero_oublie', 'zero_en_trop'], s: [0, 1] } },    // 9, 180, 270 km/h ; 25 et 5 m/s
    234: { ks: [4, 6, 10], aff: { f: ['zero_oublie', 'zero_en_trop'], s: [0, 1] } },     // 36, 54, 90 km/h ; 100 et 1,5 m/s
    345: { ks: [4, 6, 10], aff: { f: ['zero_en_trop', 'zero_en_trop'], s: [0, 1] } },    // 36, 54, 90 km/h ; 1 et 1,5 m/s
};

const composerRelier = (cfg) => {
    const kmhs = cfg.ks.map((k) => 9 * k);
    const pairs = kmhs.map((v) => ({ source: `${v} km/h`, target: `${versMs(v).txt} m/s` }));
    const leurres = cfg.leurres.map((v, i) => ({ target: `${frFromScaled(v, 2)} m/s`, code: cfg.aff.f[i] }));
    // Second chemin, sur les cibles ÉCRITES : chaque bonne cible × 3,6 redonne sa
    // vitesse (centièmes × 36 = km/h × 1 000) ; chaque leurre vaut 10 fois ou le
    // dixième de la bonne valeur de sa vitesse, et n'est égal à aucune des trois ;
    // cinq cibles distinctes ; le motif relu est celui qui a été tiré.
    const centiemes = (t) => { const [e, d = ''] = t.replace(' m/s', '').split(','); return Number(e + d.padEnd(2, '0')); };
    const egale = (t, v) => centiemes(t) * 36 === v * 1000;
    pairs.forEach((p, i) => { if (!egale(p.target, kmhs[i])) throw new Error(`gen_179 : relier ${p.source}`); });
    leurres.forEach((l, i) => {
        const vSource = kmhs[cfg.aff.s[i]];
        const attendu = l.code === 'zero_oublie' ? centiemes(l.target) * 36 === vSource * 10000 : centiemes(l.target) * 360 === vSource * 1000;
        if (!attendu || kmhs.some((v) => egale(l.target, v))) throw new Error(`gen_179 : relier, leurre ${l.target}`);
    });
    const cibles = [...pairs.map((p) => p.target), ...leurres.map((l) => l.target)];
    if (new Set(cibles).size !== 5) throw new Error(`gen_179 : relier, cibles en double ${cibles}`);
    const relu = motifDeRang(pairs.map((p) => centiemes(p.target)), leurres.map((l) => centiemes(l.target)));
    if (relu !== cfg.motif) throw new Error(`gen_179 : relier, motif ${relu} au lieu de ${cfg.motif}`);
    return { kmhs, pairs, leurres };
};

const genVitessesRelier = () => {
    // Le motif d'abord, selon ses poids (sur 100).
    let r = randInt(1, 100);
    const motif = Object.keys(POIDS_MOTIFS_RELIER).sort().find((m) => { r -= POIDS_MOTIFS_RELIER[m]; return r <= 0; });
    let cfg = tirerConfigRelier(motif);
    if (!cfg) {
        const repli = REPLIS_RELIER[motif];
        cfg = evaluerConfigRelier(repli.ks, repli.aff);
        if (!cfg || cfg.motif !== motif) throw new Error(`gen_179 : repli relier ${motif}`);
    }
    const compo = composerRelier(cfg);
    const payload = makeRelier(shuffle(compo.pairs), CONSIGNE_RELIER, { ...opts(L_VITESSE), cibles_leurres: compo.leurres });
    payload._meta.params = { kmh: compo.kmhs, motif, distracteurs: Object.fromEntries(compo.leurres.map((l) => [l.target, l.code])) };
    return calculatrice(payload);
};

// ─── 4. Quelle vitesse est égale à v km/h ? (QCM) ─────────────
// v multiple de 9, de 18 à 252 km/h (27 valeurs : la carte n'a que v pour varier).
const KMH_QCM = multiplesDe9(18, 252);

const genVitesseEgaleQcm = () => {
    const v = randomChoice(KMH_QCM);
    const cle = `${versMs(v).txt} m/s`;
    const options = [
        { t: cle, code: null },
        { t: `${frFromScaled(v * 36, 1)} m/s`, code: 'multiplie_par_3_6' },
        { t: `${frFromScaled(v, 3)} m/s`, code: 'un_seul_facteur' },
        { t: `${frFromScaled((v * 5) / 3, 2)} m/s`, code: 'minute_pour_heure' },
    ];
    // Second chemin : seule la clé, × 3,6, redonne v (centièmes de m/s × 36 = km/h × 1 000).
    const milliemes = (t) => { const [e, d = ''] = t.replace(' m/s', '').split(','); return Number(e + d.padEnd(3, '0')); };
    const justes = options.filter((o) => milliemes(o.t) * 36 === v * 10000);
    if (justes.length !== 1 || justes[0].code !== null || new Set(options.map((o) => o.t)).size !== 4) throw new Error(`gen_179 : vitesse égale ${v}`);
    const payload = makeQcm(options.map((o) => o.t), cle,
        `${RAPPEL_BROUILLON}<p><b>Quelle vitesse est égale à ${v} km/h ?</b></p>`,
        opts(L_VITESSE));
    payload._meta.params = { kmh: v, distracteurs: options.slice(1).map((o) => o.code) };
    return calculatrice(payload);
};

// ═════════ Débits ═════════

// Un débit en m³/s, mant × 10^(−dec) : de 0,01 à 75 m³/s. 2 m³/s (= 120 000
// L/min) est sacrifié.
const MANT_DEBIT = [1, 2, 3, 4, 5, 6, 8, 12, 15, 25, 35, 45, 75];
const tirerDebitM3s = () => {
    for (let essai = 0; essai < 30; essai++) {
        const mant = randomChoice(MANT_DEBIT);
        const dec = randInt(0, 2);
        if (mant === 2 && dec === 0) continue;             // 2 m³/s = 120 000 L/min, sacrifié
        return debitM3sLmin(mant, dec);
    }
    return debitM3sLmin(5, 1);
};
/** Second chemin : m³/s → L/s (× 1 000) → L/min (× 60), en entiers mis à l'échelle. */
const controlerDebit = (d) => {
    const litresParSecondeFois10dec = d.mant * 1000;          // L/s × 10^dec
    const litresParMinute = (litresParSecondeFois10dec * 60) / (10 ** d.dec);
    if (litresParMinute !== Number(d.lmin)) throw new Error(`gen_179 : débit ${d.m3s} m³/s ≠ ${d.lmin} L/min`);
    return d;
};

// ─── 5. Convertir des débits, dans les deux sens ──────────────
const genDebitConversion = () => {
    const aller = controlerDebit(tirerDebitM3s());
    let retour = controlerDebit(tirerDebitM3s());
    for (let essai = 0; essai < 20 && retour.m3s === aller.m3s; essai++) retour = controlerDebit(tirerDebitM3s());
    if (retour.m3s === aller.m3s) retour = controlerDebit(debitM3sLmin(aller.mant === 5 ? 3 : 5, 2));
    const conversions = shuffle([
        `${aller.m3s} m³/s = {{${aller.lmin}}} L/min`,
        `${retour.lmin} L/min = {{${retour.m3s}}} m³/s`,
    ]);
    const payload = makeTexteTrou(`${conversions.join(' ; ')}.`,
        `${RAPPEL_BROUILLON}<p><b>Complète ces conversions de débits.</b></p>`,
        { mode: 'saisie', ...opts(L_DEBITS) });
    payload._meta.params = { aller: `${aller.m3s} m³/s`, retour: `${retour.lmin} L/min` };
    return calculatrice(payload);
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'conversions EXACTES seulement (km/h multiples de 9 ; m/s au dixième, × 3,6 exact ; débits m³/s de 0,01 à 75, × 60 000 exact), refaites par un second chemin, throw sinon',
        'chaque distracteur est une erreur NOMMÉE (codes dans _meta.params.distracteurs) ; les sacrifiés du cours (340 m/s = 1 224 km/h) et 2 m³/s = 120 000 L/min ne sortent jamais',
    ],
    vitesse_comparer_qcm: {
        support: 'une vitesse en m/s (10 à 70, multiples de 2,5 : km/h entier) et une en km/h, égale une fois sur trois, sinon à 1 ou 2 km/h de l\'équivalent exact',
        familles: {
            egalite_supposee: '« Elles sont égales » quand elles ne le sont pas : l\'écart de 1 ou 2 km/h non vu',
            mauvaise_conversion: 'l\'une des deux vitesses désignée à tort : conversion fausse ou pas de conversion',
        },
        garantie: 'la clé est recalculée en comparant 9 × (dixièmes de m/s) et 25 × (km/h), en entiers',
    },
    vitesse_intrus_qcm: {
        support: 'v km/h, multiple de 9 de 18 à 252 ; clé v ÷ 3,6 m/s ; toutes les options en m/s',
        familles: {
            multiplie_par_3_6: 'v × 3,6 : le bon facteur dans le mauvais sens',
            un_seul_facteur: 'v ÷ 1 000 : une seule des deux unités convertie',
            minute_pour_heure: 'v ÷ 60 : 1 h prise pour 60 s (DEPP 4e 2024 q.11, « 30 min = 0,3 h »)',
        },
        supprimee: {
            intruse: 'l\'ancienne carte « L\'intruse » : l\'intruse était toujours la seule vitesse en m/s qui n\'était pas un multiple de 5 (100 %)',
        },
    },
    vitesses_plausibles_relier: {
        support: 'trois vitesses en km/h, multiples de 9 de 9 à 360, et leurs égales en m/s (entier ou ,5)',
        leurres: {
            zero_oublie: 'v × 1 000 ÷ 360 : un zéro oublié dans 3 600, dix fois la bonne valeur d\'une des trois vitesses (toujours un entier)',
            zero_en_trop: 'v × 1 000 ÷ 36 000 : un zéro en trop dans 3 600, le dixième de la bonne valeur d\'une des trois vitesses, calculé seulement sur un multiple de 18 km/h (entier ou ,5)',
        },
        tirage: 'deux leurres sur deux vitesses différentes, de la même famille ou non ; le motif de rang des trois bonnes cibles parmi les cinq triées d\'abord, selon POIDS_MOTIFS_RELIER (135 : 25 ; 123, 145, 345 : 20 ; 124 : 10 ; 234 : 5), puis une configuration de ce motif, par rejet borné (uniforme dans le motif) et repli vérifié',
        supprimees: {
            multiplie_par_3_6: 'v × 3,6 : presque jamais rond, il faisait réussir « garder les cibles rondes » (88,3 % à la passe précédente, 42 % avec les quatre familles tirées ensemble)',
            divise_par_60: 'v ÷ 60 : même raison',
        },
        strategies_sans_conversion: 'mesurées dans tmp-revue-179 : retirer les extrêmes, motif de rang, cibles rondes, mêmes chiffres (garder le plus petit, le plus grand, au hasard, l\'intrus en grandeur), rondes puis chiffres, intrus en grandeur ; « m/s plus petit que km/h » rapporté à part (c\'est le sens de la conversion, pas une ruse)',
        garantie: 'chaque bonne cible × 3,6 redonne sa vitesse, chaque leurre vaut dix fois ou le dixième de la bonne valeur de sa vitesse (relus en centièmes), aucun leurre n\'est égal à une bonne cible ni à l\'autre, cinq cibles distinctes, au plus une décimale, jamais 1 000 m/s ou plus, le motif relu sur les cibles écrites est celui tiré, « Deux vitesses sont en trop. »',
    },
};

// ─── Registry & Export ────────────────────────────────────────

const GENERATORS = {
    vitesse_saisie: genVitesseSaisie,
    vitesse_intrus_qcm: genVitesseEgaleQcm,
    vitesses_plausibles_relier: genVitessesRelier,
    vitesse_comparer_qcm: genComparerVitesse,
    debit_conversion_trous: genDebitConversion,
};

// Trois cartes depuis le 2026-09-25 ; les onglets ne disent que le format.
export const CATEGORIES = [
    {
        id: 'vitesse',
        label: L_VITESSE,
        formatsList: [
            { key: 'vitesse_saisie', label: 'Saisie' },
            { key: 'vitesse_intrus_qcm', label: 'QCM' },
            { key: 'vitesses_plausibles_relier', label: 'Relier' },
        ],
    },
    {
        id: 'comparer',
        label: L_COMPARER,
        formatsList: [{ key: 'vitesse_comparer_qcm', label: 'QCM' }],
    },
    {
        id: 'debits',
        label: L_DEBITS,
        formatsList: [{ key: 'debit_conversion_trous', label: 'Saisie trous' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
